/**
 * The five scenarios the example group rotates through — lifted from the
 * App Review seed (`apps/api/scripts/seed-review-account.ts`) so the
 * example shows the product's range (trip · roommates · couple · event ·
 * friends), not just "a trip". One scenario per app launch, in this
 * order; `selectExampleScenario` does the mapping.
 *
 * Rules the numbers follow (checked by tests):
 * - amounts are integers divisible by the member count, so every equal
 *   share is a whole number and zero-decimal currencies render cleanly;
 * - the recorded payment flows from someone in debt to someone owed;
 * - the signed-in user is always member #1; Date Nights is the one
 *   scenario where they end up owing, on purpose.
 *
 * Copy (name, tagline, settlement note, expense titles) lives in i18n
 * under `groups:exampleGroup.scenarios.<key>.*`; nothing here is shown
 * verbatim. Amounts stay the same in every currency — the seed's COP
 * figures only make sense in Colombia.
 */

export type ExampleCastMember = 'ana' | 'leo';
export type ExamplePayer = 'you' | ExampleCastMember;

export interface ExampleScenarioExpense {
  /** i18n key under `scenarios.<scenario>.expenses.` */
  key: string;
  amount: number;
  paidBy: ExamplePayer;
  category: string;
  /**
   * What the predictor would have set on a real expense of this title —
   * it drives the row icon (flight → plane, tram → tram). Must be a key
   * the shared taxonomy knows; a test guards against silent fallbacks.
   */
  subcategory?: string;
  daysAgo: number;
}

export type ExampleScenarioChipKey =
  'trip' | 'flat' | 'couple' | 'party' | 'sport';

export interface ExampleScenario {
  key: 'barcelona' | 'apartment' | 'dateNights' | 'birthday' | 'padel';
  /** The short label the scenario chips show for it. */
  chipKey: ExampleScenarioChipKey;
  /** Group category — drives the gradient/icon when a cover is missing. */
  category: string;
  /** Friends besides you. Two makes a trio; one makes a couple. */
  cast: readonly ExampleCastMember[];
  createdDaysAgo: number;
  expenses: readonly ExampleScenarioExpense[];
  /** One completed payment, debtor → creditor. */
  settlement: {
    from: ExamplePayer;
    to: ExamplePayer;
    amount: number;
    daysAgo: number;
  };
}

export const EXAMPLE_SCENARIOS: readonly ExampleScenario[] = [
  {
    key: 'barcelona',
    chipKey: 'trip',
    category: 'trip',
    cast: ['ana', 'leo'],
    createdDaysAgo: 10,
    expenses: [
      {
        key: 'flights',
        amount: 360,
        paidBy: 'you',
        category: 'transport',
        subcategory: 'flight',
        daysAgo: 9,
      },
      {
        key: 'apartment',
        amount: 240,
        paidBy: 'ana',
        category: 'accommodation',
        subcategory: 'apartment',
        daysAgo: 8,
      },
      {
        key: 'dinner',
        amount: 96,
        paidBy: 'leo',
        category: 'food',
        subcategory: 'restaurant',
        daysAgo: 3,
      },
      {
        key: 'groceries',
        amount: 45,
        paidBy: 'you',
        category: 'shopping',
        subcategory: 'groceries',
        daysAgo: 2,
      },
      {
        key: 'tram',
        amount: 18,
        paidBy: 'ana',
        category: 'transport',
        subcategory: 'tram',
        daysAgo: 1,
      },
    ],
    // you +52 · Ana +5 · Leo −57
    settlement: { from: 'leo', to: 'you', amount: 100, daysAgo: 1 },
  },
  {
    key: 'apartment',
    chipKey: 'flat',
    category: 'roommates',
    cast: ['ana', 'leo'],
    createdDaysAgo: 14,
    expenses: [
      {
        key: 'rent',
        amount: 900,
        paidBy: 'you',
        category: 'accommodation',
        subcategory: 'rent',
        daysAgo: 12,
      },
      {
        key: 'internet',
        amount: 60,
        paidBy: 'ana',
        category: 'utilities',
        subcategory: 'internet',
        daysAgo: 9,
      },
      {
        key: 'groceries',
        amount: 87,
        paidBy: 'leo',
        category: 'food',
        subcategory: 'groceries',
        daysAgo: 4,
      },
      {
        key: 'electricity',
        amount: 93,
        paidBy: 'you',
        category: 'utilities',
        subcategory: 'electricity',
        daysAgo: 2,
      },
    ],
    // you +313 · Ana −20 · Leo −293
    settlement: { from: 'ana', to: 'you', amount: 300, daysAgo: 1 },
  },
  {
    key: 'dateNights',
    chipKey: 'couple',
    category: 'couple',
    cast: ['ana'],
    createdDaysAgo: 12,
    expenses: [
      {
        key: 'sushi',
        amount: 76,
        paidBy: 'ana',
        category: 'food',
        subcategory: 'sushi',
        daysAgo: 8,
      },
      {
        key: 'cinema',
        amount: 32,
        paidBy: 'ana',
        category: 'entertainment',
        subcategory: 'movies',
        daysAgo: 5,
      },
      {
        key: 'brunch',
        amount: 42,
        paidBy: 'you',
        category: 'food',
        subcategory: 'breakfast',
        daysAgo: 1,
      },
    ],
    // you −13 · Ana +13 — the one scenario where you owe
    settlement: { from: 'you', to: 'ana', amount: 20, daysAgo: 3 },
  },
  {
    key: 'birthday',
    chipKey: 'party',
    category: 'events',
    cast: ['ana', 'leo'],
    createdDaysAgo: 11,
    expenses: [
      {
        key: 'venue',
        amount: 300,
        paidBy: 'ana',
        category: 'entertainment',
        subcategory: 'events',
        daysAgo: 10,
      },
      {
        key: 'cake',
        amount: 60,
        paidBy: 'leo',
        category: 'food',
        subcategory: 'dessert',
        daysAgo: 3,
      },
      {
        key: 'decorations',
        amount: 45,
        paidBy: 'leo',
        category: 'shopping',
        subcategory: 'gifts',
        daysAgo: 3,
      },
      {
        key: 'drinks',
        amount: 120,
        paidBy: 'you',
        category: 'drinks',
        subcategory: 'drinks',
        daysAgo: 1,
      },
    ],
    // you −5 · Ana +75 · Leo −70
    settlement: { from: 'you', to: 'ana', amount: 50, daysAgo: 1 },
  },
  {
    key: 'padel',
    chipKey: 'sport',
    category: 'friends',
    cast: ['ana', 'leo'],
    createdDaysAgo: 21,
    expenses: [
      {
        key: 'court',
        amount: 48,
        paidBy: 'you',
        category: 'entertainment',
        subcategory: 'sports',
        daysAgo: 7,
      },
      {
        key: 'balls',
        amount: 12,
        paidBy: 'ana',
        category: 'shopping',
        subcategory: 'sports',
        daysAgo: 7,
      },
      {
        key: 'drinks',
        amount: 36,
        paidBy: 'leo',
        category: 'drinks',
        subcategory: 'bar',
        daysAgo: 2,
      },
    ],
    // you +6 · Ana −10 · Leo +4
    settlement: { from: 'ana', to: 'you', amount: 10, daysAgo: 1 },
  },
];

/**
 * Launch count → scenario. Launch 1 (and "no launch recorded yet") is
 * Barcelona, so the first run and the store screenshots are stable; each
 * later launch advances one step and wraps. A scenario the user picked
 * from the chips this launch wins over the rotation.
 */
export function selectExampleScenario(
  launches: number,
  override?: ExampleScenario['key'] | null
): ExampleScenario {
  const picked = override && EXAMPLE_SCENARIOS.find(s => s.key === override);
  if (picked) return picked;
  const index = Math.max(launches - 1, 0) % EXAMPLE_SCENARIOS.length;
  return EXAMPLE_SCENARIOS[index];
}
